// hooks
import { useCurrencies } from './useCurrencies';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { CsvField, FormName } from '../@types';

export interface GetDefaultFieldsProps {
	form: FormName;
}

export function useEntityForms() {
	// hooks
	const { t } = useTranslation();
	const { currencies } = useCurrencies();

	// utils
	const getDefaultFields = ({ form }: GetDefaultFieldsProps) => {
		const fieldName: CsvField = {
			examples: [],
			header: null,
			isEditable: false,
			isRequired: true,
			label: t('name'),
			name: 'name',
			type: 'TEXT',
		};

		const fieldUniqueId: CsvField = {
			examples: [],
			header: null,
			helper: t('idExternal.helper'),
			isEditable: false,
			isRequired: true,
			label: t('uniqueIdentifier'),
			name: 'idExternal',
			type: 'TEXT',
		};

		const fieldsUser: CsvField[] = [
			{
				examples: [],
				header: null,
				isEditable: false,
				isRequired: true,
				label: t('firstName'),
				name: 'user-firstName',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				isEditable: false,
				isRequired: false,
				label: t('lastName'),
				name: 'user-lastName',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				helper: t('email.helper'),
				isEditable: false,
				isRequired: false,
				label: t('email'),
				name: 'user-email',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				helper: t('phone.helper'),
				isEditable: false,
				isRequired: false,
				label: t('phone'),
				name: 'user-phone',
				type: 'TEXT',
			},
		];

		const fieldsAddress: CsvField[] = [
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: true,
				isRequired: false,
				label: `${t('address')} 1`,
				name: 'metadata-address1',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: true,
				isRequired: false,
				label: `${t('address')} 2`,
				name: 'metadata-address2',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: true,
				isRequired: false,
				label: t('city'),
				name: 'metadata-city',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: true,
				isRequired: false,
				label: t('country'),
				name: 'metadata-country',
				type: 'TEXT',
			},
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: true,
				isRequired: false,
				label: t('postcode'),
				name: 'metadata-code',
				type: 'TEXT',
			},
		];

		const fieldsBenefits: CsvField[] = [
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: false,
				isRequired: false,
				label: t('totalBenefits'),
				name: 'metadata-totalBenefits',
				type: 'NUMBER',
			},
			{
				examples: [],
				header: null,
				isEditable: false,
				isProtected: false,
				isRequired: false,
				label: t('currency'),
				name: 'metadata-currency',
				options: currencies?.map((currency) => ({ label: currency, value: currency })) || [],
				type: 'SELECT',
			},
		];

		const fieldsBeneficiaryOrg: CsvField[] = [
			{
				examples: [],
				header: null,
				isEditable: true,
				isProtected: true,
				isRequired: false,
				label: t('businessRegistrationId'),
				name: 'metadata-businessRegistrationId',
				type: 'TEXT',
			},
		];

		if (form === 'BENEFICIARY_INDIVIDUAL') {
			return [fieldUniqueId, ...fieldsUser, ...fieldsAddress, ...fieldsBenefits];
		} else if (form === 'BENEFICIARY_HOUSEHOLD') {
			return [fieldUniqueId, fieldName, ...fieldsUser, ...fieldsAddress, ...fieldsBenefits];
		} else if (form === 'BENEFICIARY_BANK') {
			return [
				fieldUniqueId,
				fieldName,
				...fieldsUser,
				...fieldsBeneficiaryOrg,
				...fieldsAddress,
				...fieldsBenefits,
			];
		} else if (form === 'BENEFICIARY_PRIVATE_COMPANY') {
			return [
				fieldUniqueId,
				fieldName,
				...fieldsUser,
				...fieldsBeneficiaryOrg,
				...fieldsAddress,
				...fieldsBenefits,
			];
		} else if (form === 'BENEFICIARY_ORGANIZATION') {
			return [
				fieldUniqueId,
				fieldName,
				...fieldsUser,
				...fieldsBeneficiaryOrg,
				...fieldsAddress,
				...fieldsBenefits,
			];
		} else if (form === 'SUPPLIER') {
			return [fieldUniqueId, fieldName, ...fieldsUser, ...fieldsAddress];
		}

		return [];
	};
	return { currencies, getDefaultFields };
}
